import type { FlowDefinition } from '../types/index.js';
import {
  triggerNode,
  agentTaskNode,
  actionNode,
  approvalNode,
  routerNode,
  edge,
  conditionalEdge,
} from '../helpers.js';
import { FLOW_TEMPLATE_SYSTEM_ORG_ID } from './constants.js';
import {
  LINEAR_DEV_PLAN_PROMPT,
  LINEAR_DEV_IMPLEMENT_PROMPT,
  LINEAR_DEV_REVIEW_PROMPT,
  LINEAR_DEV_REVISE_PROMPT,
} from './prompts/linear-dev.js';

/**
 * Linear workflow states this flow drives the issue through. They match the
 * custom states on a Reflex-style Linear team; on other teams a user edits the
 * `state` params (and the trigger filter) to their own state names after
 * instantiating the template. `update_issue` resolves these names to state ids.
 */
export const LINEAR_DEV_STATES = {
  trigger: 'Ready to Implement',
  planning: 'Planning',
  inProgress: 'In Progress',
  agentReview: 'In Agent Review',
  inReview: 'In Review',
  rejected: 'Todo',
} as const;

/** Router labels for the self-review loop decision. */
export const LINEAR_DEV_REVIEW_LABELS = {
  approved: 'approved',
  changesRequested: 'changes_requested',
} as const;

/** Stable node ids (exported for tests and node-status mapping). */
export const LINEAR_DEV_NODES = {
  trigger: 'trigger',
  markPlanning: 'mark-planning',
  plan: 'plan',
  planReview: 'plan-review',
  markInProgress: 'mark-in-progress',
  implement: 'implement',
  implementRouter: 'implement-router',
  markAgentReview: 'mark-agent-review',
  review: 'review',
  reviewRouter: 'review-router',
  revise: 'revise',
  reviseRouter: 'revise-router',
  markInReview: 'mark-in-review',
  comment: 'comment',
  rejectToTodo: 'reject-to-todo',
  failureComment: 'failure-comment',
  markFailedTodo: 'mark-failed-todo',
} as const;

/**
 * Agent-reported outcome labels for the fallible stages. Each stage's outcome
 * router branches on its self-reported result; the shared `failed` label
 * (`something_went_wrong`) routes to the single failure sink. A hard agent
 * crash surfaces as an engine error and fails the run; this only covers an
 * agent completing and reporting it could not finish.
 */
export const LINEAR_DEV_OUTCOME_LABELS = {
  implemented: 'pr_opened',
  revised: 'revised',
  failed: 'something_went_wrong',
} as const;

/** Flow-run `state` keys this template writes and reads. */
export const LINEAR_DEV_STATE_KEYS = {
  failureReason: 'failureReason',
} as const;

/** Bounds the implement/self-review loop so it can't spin forever. */
const MAX_REVIEW_ROUNDS = 4;

/** Build a `linear.update_issue` action node that transitions the issue's state. */
function statusNode(id: string, name: string, state: string, position: { x: number; y: number }) {
  return actionNode(
    id,
    name,
    {
      provider: 'linear',
      operation: 'update_issue',
      params: {
        connection_id: '{{trigger.connectionId}}',
        issue_id: '{{trigger.issueId}}',
        state,
      },
    },
    { position },
  );
}

/**
 * Build an `agent_task` stage. Every stage in this flow shares the same launch
 * shape: an explicit (user-set) repo, structured JSON output under `result`,
 * and memory reads. The empty `repoSlug` is intentional — a strict validation
 * rule forces the user to pick the repo before saving.
 *
 * `saveToMemory` is off by default so the tight review/revise loop does not
 * persist transient state into cross-run memory; the durable plan and implement
 * stages opt in. `stateReasonField` names the output field a fallible stage
 * writes into `state.failureReason`, so the shared failure sink can report the
 * agent's own explanation.
 */
function agentStageNode(
  id: string,
  name: string,
  promptTemplate: string,
  jsonSchema: Record<string, unknown>,
  opts: {
    position: { x: number; y: number };
    joinType?: 'and' | 'or';
    maxIterations?: number;
    saveToMemory?: boolean;
    stateReasonField?: string;
  },
) {
  const { saveToMemory = false, stateReasonField, ...nodeOpts } = opts;
  return agentTaskNode(
    id,
    name,
    {
      promptTemplate,
      repoSource: 'explicit',
      repoSlug: '',
      responseFormat: 'json',
      outputKey: 'result',
      jsonSchema,
      useMemory: true,
      saveToMemory,
      ...(stateReasonField
        ? { stateOutputMapping: { [LINEAR_DEV_STATE_KEYS.failureReason]: stateReasonField } }
        : {}),
    },
    nodeOpts,
  );
}

/** Conditional edge from a stage's outcome router to the shared failure sink. */
function failEdge(id: string, fromRouter: string) {
  return conditionalEdge(
    id,
    fromRouter,
    LINEAR_DEV_NODES.failureComment,
    LINEAR_DEV_OUTCOME_LABELS.failed,
    'Failed',
  );
}

/**
 * Linear Dev Flow: a status-driven, human-gated software development lifecycle.
 *
 * Graph:
 *   trigger (issue moved to "Ready to Implement")
 *     -> mark "Planning" -> plan (agent) -> plan-review (human approval)
 *       -(approved)-> mark "In Progress" -> implement (agent, opens draft PR)
 *         -> implement-router
 *             -(pr_opened)-> mark "In Agent Review" -> [ review (agent) -> router ]
 *                 -(changes_requested)-> revise (agent) -> revise-router
 *                     -(revised)-> review                          (loop)
 *                     -(something_went_wrong)-> failure sink
 *                 -(approved)-> mark "In Review" -> comment (PR link)
 *                 -(something_went_wrong)-> failure sink
 *             -(something_went_wrong)-> failure sink
 *       -(rejected)-> mark "Todo"
 *
 *   failure sink = Report Failure (comments {{state.failureReason}}) -> mark "Todo"
 *
 * Each agent stage runs on a devbox with the target repo checked out. The
 * agents own the GitHub PR lifecycle (open draft, push fixes, mark ready) since
 * there are no GitHub PR flow actions; the flow orchestrates the lifecycle and
 * writes Linear status as it goes. Only loop-safe Linear actions are used here
 * (status writes never re-match the "Ready to Implement" trigger; comments are
 * inert), so the flow does not depend on a cross-plugin loop guard.
 *
 * The implement, revise, and review stages can self-report
 * `something_went_wrong`; each stage's outcome router sends that to a single
 * failure sink — a Report Failure comment carrying the agent's own explanation
 * (written into `state.failureReason`) followed by mark "Todo" — instead of
 * stranding the issue. Failures outside that self-reported path — a hard agent
 * crash, or the review/revise loop exhausting `MAX_REVIEW_ROUNDS` without an
 * approval — surface as an engine error and fail the run, leaving the issue in
 * "In Agent Review".
 *
 * NOTE: every agent node ships with `repoSource: 'explicit'` and an empty
 * `repoSlug` — the user sets the target repo in each agent's Launch
 * Configuration before saving. A strict validation rule blocks save/publish
 * until every explicit repo is set.
 */
export function createLinearDevFlow(id: string = 'linear-dev-flow'): FlowDefinition {
  const now = Date.now();

  return {
    id,
    name: 'Linear Dev Automation',
    description:
      'Status-driven dev lifecycle: when a Linear issue moves to "Ready to Implement", an agent plans the work for human approval, implements it behind a draft PR, self-reviews in a loop until clean, then marks the PR ready and the issue "In Review". Pick which assignees the trigger watches and set the target repo in each agent\'s Launch Configuration before saving. Requires Linear states named Ready to Implement, Planning, In Progress, In Agent Review, In Review (edit the trigger filter and state params to match your team).',
    nodes: [
      triggerNode(
        LINEAR_DEV_NODES.trigger,
        'On "Ready to Implement"',
        {
          kind: 'event',
          source: 'linear',
          event: 'linear_issue',
          // Fire only on a state *transition* into "Ready to Implement" —
          // `linear_issue` fires on every issue event, and the flow's own
          // non-status writes must never re-match (no self-trigger loop).
          filter: {
            mode: 'conditions',
            match: 'all',
            conditions: [
              {
                path: 'trigger.stateName',
                op: 'equals',
                value: LINEAR_DEV_STATES.trigger,
                // No state on the payload means this was not a transition into
                // the trigger state — don't fire.
                ifMissing: 'fail',
              },
              {
                // Assignee gate, configured per creator via the trigger
                // filter's Assignee condition. Ships as an empty allow-list
                // with `ifMissing:'fail'` — a never-matching placeholder that
                // the strict validator (TRIGGER_FILTER_NEVER_MATCHES) blocks
                // save on until the creator picks assignees or removes the
                // condition (= any assignee).
                path: 'trigger.assigneeId',
                op: 'in',
                value: [],
                ifMissing: 'fail',
              },
              {
                // Creates and non-status edits carry no stateId here — fail closed.
                path: 'trigger.changedFields',
                op: 'intersects',
                value: ['stateId'],
                ifMissing: 'fail',
              },
            ],
          },
        },
        { position: { x: 0, y: 0 } },
      ),

      statusNode(LINEAR_DEV_NODES.markPlanning, 'Mark "Planning"', LINEAR_DEV_STATES.planning, {
        x: 0,
        y: 140,
      }),

      agentStageNode(
        LINEAR_DEV_NODES.plan,
        'Plan Implementation',
        LINEAR_DEV_PLAN_PROMPT,
        {
          type: 'object',
          properties: {
            plan: { type: 'string' },
            summary: { type: 'string' },
          },
          required: ['plan', 'summary'],
        },
        { position: { x: 0, y: 280 }, saveToMemory: true },
      ),

      approvalNode(
        LINEAR_DEV_NODES.planReview,
        'Review Plan',
        {
          approvalType: 'plan_review',
          prompt:
            'Review the implementation plan for {{trigger.issueIdentifier}}: {{trigger.issueTitle}}.\n\nReadiness: {{nodes.plan.output.summary}}\n\n{{nodes.plan.output.plan}}',
          allowFeedback: true,
        },
        { position: { x: 0, y: 420 } },
      ),

      statusNode(
        LINEAR_DEV_NODES.markInProgress,
        'Mark "In Progress"',
        LINEAR_DEV_STATES.inProgress,
        { x: 0, y: 560 },
      ),

      agentStageNode(
        LINEAR_DEV_NODES.implement,
        'Implement (draft PR)',
        LINEAR_DEV_IMPLEMENT_PROMPT,
        {
          type: 'object',
          properties: {
            status: {
              type: 'string',
              enum: [LINEAR_DEV_OUTCOME_LABELS.implemented, LINEAR_DEV_OUTCOME_LABELS.failed],
            },
            pr: { type: 'string' },
            summary: { type: 'string' },
          },
          required: ['status', 'summary'],
        },
        { position: { x: 0, y: 700 }, saveToMemory: true, stateReasonField: 'summary' },
      ),

      // Branch on the implement stage's self-reported status: a draft PR moves
      // on to review; a reported failure routes to human follow-up rather than
      // sending an empty PR into the review loop.
      routerNode(
        LINEAR_DEV_NODES.implementRouter,
        'Implement Outcome',
        {
          strategy: 'expression',
          expression: '{{nodes.implement.output.status}}',
          labels: [LINEAR_DEV_OUTCOME_LABELS.implemented, LINEAR_DEV_OUTCOME_LABELS.failed],
        },
        { position: { x: 360, y: 700 } },
      ),

      statusNode(
        LINEAR_DEV_NODES.markAgentReview,
        'Mark "In Agent Review"',
        LINEAR_DEV_STATES.agentReview,
        { x: 0, y: 840 },
      ),

      agentStageNode(
        LINEAR_DEV_NODES.review,
        'Self-review PR',
        LINEAR_DEV_REVIEW_PROMPT,
        {
          type: 'object',
          properties: {
            verdict: {
              type: 'string',
              enum: [
                LINEAR_DEV_REVIEW_LABELS.approved,
                LINEAR_DEV_REVIEW_LABELS.changesRequested,
                LINEAR_DEV_OUTCOME_LABELS.failed,
              ],
            },
            feedback: { type: 'string' },
            pr: { type: 'string' },
          },
          required: ['verdict', 'feedback'],
        },
        // Joined by `or`: the first pass arrives from mark-agent-review, later
        // passes loop back from revise. The default `and` join would wait for
        // both and deadlock the first round (mirrors the profile flow's build).
        {
          position: { x: 0, y: 980 },
          joinType: 'or',
          maxIterations: MAX_REVIEW_ROUNDS,
          stateReasonField: 'feedback',
        },
      ),

      routerNode(
        LINEAR_DEV_NODES.reviewRouter,
        'Review Verdict',
        {
          strategy: 'expression',
          expression: '{{nodes.review.output.verdict}}',
          labels: [
            LINEAR_DEV_REVIEW_LABELS.approved,
            LINEAR_DEV_REVIEW_LABELS.changesRequested,
            LINEAR_DEV_OUTCOME_LABELS.failed,
          ],
        },
        { position: { x: 0, y: 1120 }, maxIterations: MAX_REVIEW_ROUNDS },
      ),

      agentStageNode(
        LINEAR_DEV_NODES.revise,
        'Revise PR',
        LINEAR_DEV_REVISE_PROMPT,
        {
          type: 'object',
          properties: {
            status: {
              type: 'string',
              enum: [LINEAR_DEV_OUTCOME_LABELS.revised, LINEAR_DEV_OUTCOME_LABELS.failed],
            },
            summary: { type: 'string' },
          },
          required: ['status', 'summary'],
        },
        {
          position: { x: 360, y: 1120 },
          maxIterations: MAX_REVIEW_ROUNDS,
          stateReasonField: 'summary',
        },
      ),

      // Branch on the revise stage's status: a successful revision loops back to
      // review, a reported failure routes to human follow-up. Sits in the
      // review/revise cycle, so it carries the same iteration guard.
      routerNode(
        LINEAR_DEV_NODES.reviseRouter,
        'Revise Outcome',
        {
          strategy: 'expression',
          expression: '{{nodes.revise.output.status}}',
          labels: [LINEAR_DEV_OUTCOME_LABELS.revised, LINEAR_DEV_OUTCOME_LABELS.failed],
        },
        { position: { x: 360, y: 1260 }, maxIterations: MAX_REVIEW_ROUNDS },
      ),

      statusNode(LINEAR_DEV_NODES.markInReview, 'Mark "In Review"', LINEAR_DEV_STATES.inReview, {
        x: -360,
        y: 1260,
      }),

      actionNode(
        LINEAR_DEV_NODES.comment,
        'Comment PR Link',
        {
          provider: 'linear',
          operation: 'create_issue_comment',
          params: {
            connection_id: '{{trigger.connectionId}}',
            issue_id: '{{trigger.issueId}}',
            body: 'Implementation complete and self-reviewed. PR is ready for review: {{nodes.implement.output.pr}}',
          },
        },
        { position: { x: -360, y: 1400 } },
      ),

      statusNode(
        LINEAR_DEV_NODES.rejectToTodo,
        'Plan Rejected: Mark "Todo"',
        LINEAR_DEV_STATES.rejected,
        {
          x: 360,
          y: 560,
        },
      ),

      // Shared failure sink: reached when any fallible stage (implement, revise,
      // or review) self-reports `something_went_wrong`. Joined by `or` because
      // its predecessors are those stages' outcome routers, only one of which
      // fires in a run. The body reports the failing agent's own explanation,
      // which each stage writes into `state.failureReason`.
      actionNode(
        LINEAR_DEV_NODES.failureComment,
        'Report Failure',
        {
          provider: 'linear',
          operation: 'create_issue_comment',
          params: {
            connection_id: '{{trigger.connectionId}}',
            issue_id: '{{trigger.issueId}}',
            body: 'Automated work could not complete: {{state.failureReason}}\n\nMoving this issue back to "Todo" for a human to pick up.',
          },
        },
        { position: { x: 720, y: 980 }, joinType: 'or' },
      ),

      statusNode(LINEAR_DEV_NODES.markFailedTodo, 'Mark "Todo"', LINEAR_DEV_STATES.rejected, {
        x: 720,
        y: 1120,
      }),
    ],
    edges: [
      edge('e-trigger-planning', LINEAR_DEV_NODES.trigger, LINEAR_DEV_NODES.markPlanning),
      edge('e-planning-plan', LINEAR_DEV_NODES.markPlanning, LINEAR_DEV_NODES.plan),
      edge('e-plan-review', LINEAR_DEV_NODES.plan, LINEAR_DEV_NODES.planReview),
      conditionalEdge(
        'e-plan-approved',
        LINEAR_DEV_NODES.planReview,
        LINEAR_DEV_NODES.markInProgress,
        'approved',
        'Approve plan',
      ),
      conditionalEdge(
        'e-plan-rejected',
        LINEAR_DEV_NODES.planReview,
        LINEAR_DEV_NODES.rejectToTodo,
        'rejected',
        'Reject plan',
      ),
      edge('e-inprogress-implement', LINEAR_DEV_NODES.markInProgress, LINEAR_DEV_NODES.implement),
      edge('e-implement-router', LINEAR_DEV_NODES.implement, LINEAR_DEV_NODES.implementRouter),
      conditionalEdge(
        'e-implement-ok',
        LINEAR_DEV_NODES.implementRouter,
        LINEAR_DEV_NODES.markAgentReview,
        LINEAR_DEV_OUTCOME_LABELS.implemented,
        'PR opened',
      ),
      failEdge('e-implement-failed', LINEAR_DEV_NODES.implementRouter),
      edge('e-agentreview-review', LINEAR_DEV_NODES.markAgentReview, LINEAR_DEV_NODES.review),
      edge('e-review-router', LINEAR_DEV_NODES.review, LINEAR_DEV_NODES.reviewRouter),
      conditionalEdge(
        'e-router-revise',
        LINEAR_DEV_NODES.reviewRouter,
        LINEAR_DEV_NODES.revise,
        LINEAR_DEV_REVIEW_LABELS.changesRequested,
        'Changes requested',
      ),
      conditionalEdge(
        'e-router-inreview',
        LINEAR_DEV_NODES.reviewRouter,
        LINEAR_DEV_NODES.markInReview,
        LINEAR_DEV_REVIEW_LABELS.approved,
        'Approved',
      ),
      failEdge('e-review-failed', LINEAR_DEV_NODES.reviewRouter),
      edge('e-revise-router', LINEAR_DEV_NODES.revise, LINEAR_DEV_NODES.reviseRouter),
      conditionalEdge(
        'e-revise-ok',
        LINEAR_DEV_NODES.reviseRouter,
        LINEAR_DEV_NODES.review,
        LINEAR_DEV_OUTCOME_LABELS.revised,
        'Revised',
      ),
      failEdge('e-revise-failed', LINEAR_DEV_NODES.reviseRouter),
      edge('e-inreview-comment', LINEAR_DEV_NODES.markInReview, LINEAR_DEV_NODES.comment),
      edge('e-failed-todo', LINEAR_DEV_NODES.failureComment, LINEAR_DEV_NODES.markFailedTodo),
    ],
    enabled: true,
    organizationId: FLOW_TEMPLATE_SYSTEM_ORG_ID,
    createdAt: now,
    updatedAt: now,
  };
}
